import React, { useEffect, useMemo, useState } from "react";
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
    ColumnDef,
    Header,
    FilterFn,
} from "@tanstack/react-table";
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Select,
    MenuItem,
    Paper,
    Toolbar,
    TextField,
    SelectChangeEvent,
    Box,
} from "@mui/material";
import moment from "moment";
import { handleExportClick } from "../../ConsolidatedLogs/ConsolidatedController";
import { Tooltip } from "@mui/material";
import { ScrollView, TouchableOpacity, View } from "react-native";
import { IconButton, MD3Colors } from "react-native-paper";
import ControlStyles from "./ControlStyles";
import ButtonControl from "./Button";
import { useNav } from "../../../../providers/NavProvider";
import { useAuth } from "../../../../providers/AuthProvider";
import { deleteItem, getAllItems } from "../service/dataOperations";
import { useClient } from "../../../../providers/ClientProvider";
import ToastMessage from "./Toast";
import ConfirmBox from "../../../Common/ConfirmBox";
import WeeklyReportModal from "../forms/WeeklyReportModal";
import { de } from "react-native-paper-dates";
import { DropdownForTanStackFilter } from "./DropdownForTanStackFilter";
import { FilterChipsBar } from "./FilterChipsBar";

interface Props {
    data: Array<any>;
    columns: Array<any>;
    updateFormState: () => void;
    updateFormMode: (val: string) => void;
    handleRowPress: (val: Number[]) => void;
    ViewPressAction: (val: any) => void;
    EditPressAction: (val: any) => void;
    NewPressAction: () => void;
    siteId: string;
    logTitle: string;
    isConsolidatedTable: boolean;
    setRefreshData?: (e: boolean) => void;
    selectedYear?: string;
    isFormOpen?: boolean;
}

const ReactDataTable: React.FC<Props> = (props) => {

    const [showReportModal, setShowReportModal] = useState(false);
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [data, setData] = useState<any[]>([]);
    const memoizedData = useMemo(() => props.data, [props.data.length]);
    const [permissions, setpermissions] = useState("read");
    const [canWriteData, setCanWriteData] = useState(false);
    const [siteTitle, setSiteTitle] = useState("")
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [toastVisibility, setToastVisibility] = useState<boolean>(false)
    const [toastText, setToastText] = useState<string>("");
    const { clientAPIURL } = useClient();
    const { sitesResp } = useNav();
    const { getAccessToken } = useAuth();

    function findParentSite(siteresp: any, siteId: string, parent: any = null): any | null {
        if (!siteresp?.children?.length) {
            return null; // No children, return null
        }

        for (const child of siteresp.children) {
            if (child.id === siteId) {
                return parent; // Return the direct parent
            }

            // Recursively search in child nodes
            const foundParent = findParentSite(child, siteId, child);
            if (foundParent) return foundParent;
        }

        return null; // Parent not found
    }

    function findSiteById(siteresp: any, siteId: string) {
        if (!siteresp.children || siteresp.children.length === 0) {
            return null; // No children, return null
        }

        for (let child of siteresp.children) {
            if (child.id === siteId) {
                return child; // Found the matching site
            }

            // Recursively search in child nodes
            const foundSite: any = findSiteById(child, siteId);
            if (foundSite) return foundSite;
        }

        return null; // Site not found in any children
    }

    useEffect(() => {
        if (!props.isFormOpen) {
            setSelectedRows([]);
        }
    }, [props.isFormOpen])

    useEffect(() => {
        if (sitesResp) {
            const parentSite = findParentSite(sitesResp, props.siteId);
            const matchedSite = findSiteById(sitesResp, props.siteId);
            parentSite && parentSite.title && setSiteTitle(parentSite.title);
            //console.info(matchedSite)
            matchedSite && matchedSite.permissions && setpermissions(JSON.stringify(matchedSite.permissions && matchedSite.permissions).toLowerCase())


        }
    }, [sitesResp]);

    useEffect(() => {
        setCanWriteData(
            permissions.includes('admin') ||
            permissions.includes('manage') ||
            permissions.includes('write') ||
            permissions.includes('contribute')
        );
    }, [permissions]);
    useEffect(() => {
        if (props.data) {
            const processed = props.data
                .sort((a, b) => b.ID - a.ID)
                .map((item) => {
                    // Handle Attachments
                    let attachments: any[] = [];
                    try {
                        attachments = item.Attachments ? JSON.parse(item.Attachments) : [];
                    } catch (e) {
                        attachments = [];
                    }
                    const attachmentsCount =
                        attachments && attachments.length > 0 ? attachments.length : 0;

                    // Handle Location
                    let locationLink = item.Location;
                    try {
                        if (item.Location && item.Location.includes("coords")) {
                            const locObj = JSON.parse(item.Location || "{}");
                            if (locObj?.coords) {
                                const { latitude, longitude } = locObj.coords;
                                locationLink = `https://maps.google.com/?q=${latitude},${longitude}`;
                            }
                        }
                    } catch (e) {
                        locationLink = "";
                    }

                    return {
                        ...item,
                        Attachments: attachmentsCount,
                        Location: locationLink || "",
                    };
                });

            setData(processed);
        }
    }, [props.data]);
    const arrIncludesSomeExact: FilterFn<any> = (row, columnId, filterValue) => {
        const selected = (filterValue ?? []) as string[];
        if (!selected.length) return true;

        const normalize = (v: unknown) =>
            String(v ?? "")
                .trim()
                .toLowerCase()
                .replace(/\s+/g, " ")
                .replace(/[-–—]/g, "-"); // normalize hyphen variants

        const cell = normalize(row.getValue(columnId));

        return selected.some((s) => normalize(s) === cell);
    };
    const columns = useMemo<ColumnDef<any>[]>(() => {
        // Pick preferred order depending on consolidated or not
        const preferredOrder = props.isConsolidatedTable
            ? ["packageName", "Contractor", "Status", "Date", "Month", "Year"]
            : ["ID", "Status", "ClosedOut"];

        // Sort columns based on preferred order first, then fallback to `order`
        const sortedColumns = [...props.columns].sort((a, b) => {
            const indexA = preferredOrder.indexOf(a.columnName);
            const indexB = preferredOrder.indexOf(b.columnName);

            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
            }
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;

            return (a.order ?? 0) - (b.order ?? 0);
        });

        // Map into TanStack Table format
        return sortedColumns
            .filter((col) => col.columnName) // only visible
            .map((col) => ({
                accessorKey: col.columnName,
                header: col.title,
                filterFn: arrIncludesSomeExact,
                cell: (info) => {
                    let value = info.getValue();

                    if (col.columnName === "Attachments") {
                        return Number(value) > 0 ? `${value} Files` : "No Files";
                    }

                    if (col.columnName === "Location") {
                        return value ? value : value;
                    }

                    // if (["Date", "DueDate", "StartDate", "ExpiryDate", "EndDate", "DateClosedOut"].includes(col.columnName) && value) {
                    //     return moment(value, "YYYY-MM-DD").format("MM/DD/YYYY");//moment(value).format("YYYY-MM-DD");
                    // }
                    if (
                        //["Date", "DueDate", "StartDate", "ExpiryDate", "EndDate", "DateClosedOut"].includes(col.accessor) &&
                        /Date|Due|Start|End|Expiry|NextFreezeDate|ClosedOut/i.test(col.columnName) &&
                        value &&
                        moment(value, "DD/MM/YYYY", true).isValid()
                    ) {
                        value = moment(value, "DD/MM/YYYY").format("MM/DD/YYYY");
                    }

                    return value ?? "";
                },
            }));
    }, [props.columns, props.isConsolidatedTable]);



    const table = useReactTable({
        data: data,
        columns,
        state: { sorting, columnFilters, globalFilter },
        initialState: {
            pagination: { pageSize: 30 },   // sets default to 30 items per page
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const getRowKey = (original: any): string | number =>
        props.isConsolidatedTable
            ? `${original.packageName}||${original.ID}`
            : original.ID;

    const toggleRow = (original: any) => {
        const key = getRowKey(original);
        setSelectedRows((prev) =>
            prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
        );
        props.handleRowPress([original.ID]);
    };

    const handleExport = (fileName: string) => {
        const filtered = table.getFilteredRowModel().rows.map((r) => r.original);
        handleExportClick(filtered, fileName);
    };

    const actionBtnStyle = (disabled: boolean) => ({
        backgroundColor: disabled ? "#676767" : "#4BB75F",
        color: "#fff",
        marginRight: 8,
        textTransform: "none",
        "&:hover": { backgroundColor: disabled ? "#676767" : "#3a944d" },
    });

    function handleNew() {

        props.updateFormMode("New");
        //props.NewPressAction();
    };
    function handleEditPress(sRows: any) {

        if (sRows.length === 0) {
            setToastVisibility(true);
            setToastText("Please select atleast one item to view.")

        }
        else {
            props.updateFormMode("Edit");
            props.ViewPressAction(sRows);
        }
    };
    function handleViewPress(sRows: any) {
        if (sRows.length === 0) {
            setToastVisibility(true);
            setToastText("Please select atleast one item to view.")
        }
        else {
            props.updateFormMode("View");
            if (props.isConsolidatedTable) {
                // Resolve the full row from current displayed data — avoids stale TableProvider.data lookup
                const key = sRows[0];
                const [packageName, idStr] = String(key).split('||');
                const id = Number(idStr);
                const fullRow = data.find(r => r.ID === id && r.packageName === packageName);
                props.ViewPressAction(fullRow ? [fullRow] : []);
            } else {
                props.ViewPressAction(sRows);
            }
        }
    };
    async function handleConfirmDelete() {
        try {

            let accessToken = await getAccessToken();

            // Send delete requests in parallel for better performance
            const deleteRequests = selectedRows.map(async (item) =>
                await deleteItem(props.logTitle, accessToken, clientAPIURL, props.siteId, item)
                //alert(`${props.logTitle}, =  ${item}`)
            );

            const results = await Promise.all(deleteRequests);

            let allSuccess = true;

            for (const result of results) {
                if (!result) {
                    allSuccess = false;
                    continue;
                }
                if (result.status === 200) {
                    accessToken = null;
                    setToastText("Deleted successfully.");
                    setToastVisibility(true);
                    setData((prevData) => {
                        const updatedData = prevData.filter((item) => !selectedRows.includes(item.ID));
                        console.log("Updated Data:", updatedData); // Check if item 34 is removed
                        return updatedData;
                    });
                    // const updatedData = data.filter((item) => !selectedRows.includes(item.ID))
                    // data = updatedData;
                    //props.setRefreshData(true);
                } else if (result.status === 401 || result.statusText === "Unauthorized") {
                    accessToken = null;
                    setToastText("Delete Failed: You are not authorized to perform this action.");
                    setToastVisibility(true);
                } else {
                    accessToken = null;
                    setToastText("Delete failed: Unexpected error.");
                    setToastVisibility(true);
                    allSuccess = false;
                }
            }



            if (allSuccess) {
                //props.setRefreshData(true);

            }

            setShowConfirmation(false);
            setSelectedRows([]);
        } catch (error) {
            console.error("Delete Error:", error);
            setToastText("Delete failed: Network or server issue.");
            setToastVisibility(true);
            setShowConfirmation(false);
        }
    };
    function handleToastDismiss() {
        setToastVisibility(false);
    };
    function handleCancelDelete() {
        // Close the confirmation dialog without performing any action
        setShowConfirmation(false);
    };

    const isYes = (v: string = "No") => v.trim().toLowerCase() === "yes";
    const canEditSelection = (rows: any[]) => {
        if (selectedRows.length !== 1) return false;
        const item = data.find(r =>
            String(r.id ?? r.ID ?? r.key) === String(selectedRows[0])
        );
        return isYes(item?.Editable);
    }
    const canDelete = (rows: any[]) => {
        if (selectedRows?.length === 0) return false;
        return selectedRows.every((id: any) => {
            const row = data.find(r => String(r.id ?? r.ID ?? r.key) === String(id));
            if (!row) return false; // unknown row -> disallow delete
            const editable = row.isEditable ?? row.Editable;
            return isYes(editable);
        });
    }



    const FilterHeaderCell = ({
        header,
        memoizedDataProp,
    }: {
        header: Header<any, unknown>;
        memoizedDataProp: any[];
    }) => {
        const allOptions = useMemo(() => {
            const set = new Set<string>();
            for (const row of memoizedDataProp) {
                const v = row?.[header.column.id];
                if (v === null || v === undefined || v === "") continue;
                set.add(String(v));
            }
            return Array.from(set).sort();
        }, [memoizedDataProp, header.column.id]);

        const committed =
            (header.column.getFilterValue() as string[] | undefined) ?? [];

        return (
            <TableCell key={header.id} onClick={(e) => e.stopPropagation()}>
                <DropdownForTanStackFilter
                    options={allOptions}              // string[]
                    committed={committed}             // string[]
                    onCommit={(val) => header.column.setFilterValue(val)} // val: string[] | undefined
                />
            </TableCell>
        );
    };
    const clampCellSx = {
        maxWidth: 260,                 // adjust per column
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 2,            // ✅ 1 or 2 lines (your choice)
        whiteSpace: "normal",
        lineHeight: 1.25,
    };

    const colMetaById = useMemo(() => {
        const m = new Map<string, any>();
        for (const c of props.columns) m.set(c.columnName, c);
        return m;
    }, [props.columns]);
    const alwaysEditable = useMemo(() => new Set([
        'Environmental Issues Observations',
        'Package Status',
        'Permit And Licenses',
        'Contracts',
        'Recycled Materials Log',
        'Regional Materials Log',
    ]), [/* none */]);

    const isLogAlwaysEditable = alwaysEditable.has(props.logTitle);
    const selectedSet = useMemo(() => new Set(selectedRows), [selectedRows]);

    const leafHeaders = table.getHeaderGroups().at(-1)?.headers ?? [];
    const cellSx = { maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", color: "inherit" };
    const nowrapSpanStyle: React.CSSProperties = { display: "inline-block", maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

    const optionsByColumnId = useMemo(() => {
        const map = new Map<string, string[]>();
        const sets = new Map<string, Set<string>>();

        // only compute for leaf columns (visible)
        const colIds = leafHeaders.map(h => h.column.id);

        for (const id of colIds) sets.set(id, new Set());

        for (const row of memoizedData) {
            for (const id of colIds) {
                const v = row?.[id];
                if (v === null || v === undefined || v === "") continue;
                sets.get(id)!.add(String(v));
            }
        }

        for (const [id, set] of sets) {
            map.set(id, Array.from(set).sort());
        }

        return map;
    }, [memoizedData, leafHeaders]);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isRowOverdue = (row: any): boolean => {
        const { DueDate, Status } = row.original;
        if (!DueDate || Status === 'Closed' || Status === 'Escalated') return false;
        const [d, m, y] = DueDate.split('/');
        if (!d || !m || !y) return false;
        return new Date(+y, +m - 1, +d) < today;
    };

    const rowZebraBrandSx = (idx: number, row: any) => ({
        backgroundColor: idx % 2 === 0 ? '#4BB75F' : '#FFFFFF',
        ...(isRowOverdue(row) && { boxShadow: 'inset 0 0 0 2px #B71C1C, inset 4px 0 0 #B71C1C' }),
    });

    const rowLockedBrandSx = {
        opacity: 0.55,              // cheap + effective
        cursor: "not-allowed",
    };

    const cellLockedBrandSx = {
        color: "rgba(0,0,0,0.55)",   // keeps text readable on green
    };

    return (
        <Paper>
            <WeeklyReportModal
                visible={showReportModal}
                onClose={() => setShowReportModal(false)}
                apiEndpoint={clientAPIURL && clientAPIURL}
                logTitle={props.logTitle}
                siteId={props.siteId}
            />
            {/* Toolbar */}
            <Toolbar sx={{ display: "flex" }}>
                <>
                    {/* <div>
                    <Button
                        sx={actionBtnStyle(false)}
                        onClick={() => {
                            props.updateFormMode("New");
                            //props.NewPressAction();
                        }}
                    >
                        + New
                    </Button>
                    <Button
                        sx={actionBtnStyle(selectedRows.length !== 1)}
                        disabled={selectedRows.length !== 1}
                        onClick={() => {
                            props.updateFormMode("View");
                            props.ViewPressAction(selectedRows);
                        }}
                    >
                        View
                    </Button>
                    <Button
                        sx={actionBtnStyle(selectedRows.length !== 1)}
                        disabled={selectedRows.length !== 1}
                        onClick={() => {
                            props.updateFormMode("Edit");
                            props.EditPressAction(selectedRows);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        sx={actionBtnStyle(selectedRows.length === 0)}
                        disabled={selectedRows.length === 0}
                        onClick={() => props.setRefreshData?.(true)}
                    >
                        Delete
                    </Button>
                    <Button sx={actionBtnStyle(false)} onClick={handleExport}>
                        Export
                    </Button>
                </div> */}
                </>
                <View style={ControlStyles.ribbonContainer}>
                    {props.isConsolidatedTable === true ?

                        <ScrollView horizontal style={ControlStyles.ribbonScrollView}>
                            <View style={ControlStyles.buttonContainer}>
                                <ButtonControl icon="eye-outline" mode="contained"
                                    onPress={() => handleViewPress(selectedRows)}
                                    Title='View' disabled={selectedRows.length === 1 ? false : true} />
                            </View>
                            {props.logTitle === 'Report Request' ? null : <View style={ControlStyles.buttonDivider}></View>}
                            {props.logTitle === 'Report Request' ? null :
                                <View>
                                    <View style={ControlStyles.buttonContainer}>
                                        <ButtonControl icon="export-variant" mode="contained" onPress={() => handleExport(props.logTitle && `ConsolidatedData_${props.logTitle}`)} Title='Export' disabled={data.length > 0 ? false : true} />
                                    </View>
                                </View>
                            }
                        </ScrollView>
                        :

                        props.logTitle === 'Package Status' ?
                            <ScrollView horizontal style={ControlStyles.ribbonScrollView}>
                                <View><View style={ControlStyles.buttonContainer}>
                                    <ButtonControl icon="plus" mode="contained"
                                        onPress={() => handleNew()}
                                        Title='New' disabled={permissions.includes('admin') || permissions.includes('manage') ? false : true}
                                    />
                                </View></View>
                                {/* <View style={ControlStyles.buttonDivider}></View>
                                <View>
                                    <View style={ControlStyles.buttonContainer}>
                                        <ButtonControl icon="eye-outline" mode="contained"
                                            onPress={() => handleViewPress(selectedRows)}
                                            Title='View' disabled={selectedRows.length === 1 ? false : true} />
                                    </View>
                                </View> */}

                                <View style={ControlStyles.buttonDivider}></View>
                                <View><View style={ControlStyles.buttonContainer}>
                                    {permissions.includes('admin') || permissions.includes('manage') ?
                                        <ButtonControl icon="pencil" mode="contained"
                                            onPress={() => handleEditPress(selectedRows)}
                                            Title='Re-Open / Review' disabled={selectedRows.length === 1 ? false : true} />
                                        :
                                        permissions.includes('write') ?
                                            <ButtonControl icon="pencil" mode="contained"
                                                onPress={() => handleEditPress(selectedRows)}
                                                Title='Re-Submit' disabled={selectedRows.length === 1 ? false : true} /> : null
                                    }
                                </View></View>

                                <View style={ControlStyles.buttonDivider}></View>
                                <View><View style={ControlStyles.buttonContainer}>
                                    {
                                        permissions.includes('admin') ?
                                            <ButtonControl icon="delete-outline" mode="contained" onPress={() => setShowConfirmation(true)} Title='Delete' disabled={selectedRows.length === 0 ? true : false} />
                                            :
                                            <ButtonControl icon="delete-outline" mode="contained" onPress={() => setShowConfirmation(true)} Title='Delete' disabled={true} />
                                    }
                                </View></View>

                                <View style={ControlStyles.buttonDivider}></View>
                                <View>
                                    <View style={ControlStyles.buttonContainer}>
                                        <ButtonControl icon="export-variant" mode="contained" onPress={() => handleExport(props.logTitle && `${siteTitle}_${props.logTitle}`)} Title='Export' disabled={data.length > 0 ? false : true} />
                                    </View>
                                </View>
                            </ScrollView>
                            :
                            <ScrollView horizontal style={ControlStyles.ribbonScrollView}>
                                <View><View style={ControlStyles.buttonContainer}>
                                    <ButtonControl icon="plus" mode="contained"
                                        onPress={() => handleNew()}
                                        Title='New' disabled={canWriteData === true ? false : true}
                                    />
                                </View></View>
                                <View style={ControlStyles.buttonDivider}></View>
                                <View><View style={ControlStyles.buttonContainer}>
                                    <ButtonControl icon="eye-outline" mode="contained"
                                        onPress={() => handleViewPress(selectedRows)}
                                        Title='View' disabled={selectedRows.length === 1 ? false : true} />
                                </View></View>
                                {props.logTitle === 'Report Request' ? null : <View style={ControlStyles.buttonDivider}></View>}
                                {props.logTitle === 'Report Request' ? null : <View><View style={ControlStyles.buttonContainer}>
                                    {canWriteData === true ?
                                        <ButtonControl icon="pencil" mode="contained"
                                            onPress={() => handleEditPress(selectedRows)}
                                            Title='Edit' disabled={!canEditSelection(selectedRows)} />
                                        :
                                        <ButtonControl icon="pencil" mode="contained"
                                            onPress={() => handleEditPress(selectedRows)}
                                            Title='Edit' disabled={true} />
                                    }
                                </View></View>}
                                {props.logTitle === 'Report Request' ? null : <View style={ControlStyles.buttonDivider}></View>}
                                {props.logTitle === 'Report Request' ? null : <View><View style={ControlStyles.buttonContainer}>
                                    {canWriteData === true ?
                                        <ButtonControl icon="delete-outline" mode="contained" onPress={() => setShowConfirmation(true)} Title='Delete' disabled={!canDelete(selectedRows)} />
                                        :
                                        <ButtonControl icon="delete-outline" mode="contained" onPress={() => setShowConfirmation(true)} Title='Delete' disabled={true} />
                                    }
                                </View></View>}
                                {props.logTitle === 'Report Request' ? null : <View style={ControlStyles.buttonDivider}></View>}
                                {props.logTitle === 'Report Request' ? null :
                                    <View>
                                        <View style={ControlStyles.buttonContainer}>
                                            <ButtonControl icon="export-variant" mode="contained" onPress={() => handleExport(props.logTitle && `${siteTitle}_${props.logTitle}`)} Title='Export' disabled={data.length > 0 ? false : true} />
                                        </View>
                                    </View>
                                }
                                {props.logTitle === 'Environmental Issues Observations' && <View style={ControlStyles.buttonDivider}></View>}
                                {props.logTitle === 'Environmental Issues Observations' &&
                                    <View>
                                        <View style={ControlStyles.buttonContainer}>
                                            <ButtonControl icon="file-document-multiple-outline" mode="contained" onPress={() => setShowReportModal(true)} Title='Report' disabled={data.length > 0 ? false : true} />
                                        </View>
                                    </View>

                                }
                            </ScrollView>
                    }
                </View>
                {/* Global Search */}
                <TextField
                    size="small"
                    placeholder="Search..."
                    value={globalFilter ?? ""}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                />
                <View>
                    <ToastMessage
                        show={toastVisibility}
                        actionLabel="Dismiss"
                        toastMessage={toastText}
                        duration={3000}
                        onDismiss={handleToastDismiss}
                    />
                </View>
                {showConfirmation && (
                    <View>
                        <ConfirmBox 
                            title="Delete Selected Items" 
                            message="Are you sure you want to permanently delete the selected records? This action cannot be undone." 
                            yesCallback={handleConfirmDelete} 
                            noCallback={handleCancelDelete} 
                            yesText="Delete" 
                            noText="Cancel" 
                        />
                    </View>
                )}
            </Toolbar>

            {/* Table */}
            <FilterChipsBar table={table} columnsMeta={props.columns} />

            <TableContainer sx={{ fontFamily: 'Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif', maxHeight: 600 }}>
                <Table stickyHeader>
                    <TableHead>
                        {table.getHeaderGroups().map((hg) => (
                            <TableRow
                                key={hg.id}
                                sx={{ backgroundColor: "#000000", color: "#ffffff" }}
                            >
                                <TableCell
                                    sx={{
                                        backgroundColor: "#000000",
                                        color: "#ffffff",
                                        fontWeight: "bold",
                                    }}
                                    padding="checkbox"
                                >
                                    Select
                                </TableCell>

                                {hg.headers.map((header) => (
                                    <TableCell
                                        key={header.id}
                                        onClick={header.column.getToggleSortingHandler()}  // ✅ click to sort
                                        sx={{
                                            backgroundColor: "#000000",
                                            color: "#ffffff",
                                            fontWeight: "bold",
                                            cursor: header.column.getCanSort() ? "pointer" : "default",
                                            userSelect: "none",
                                        }}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}

                                        {/* ✅ Show sort indicator */}
                                        {/* {{
                                            asc: "↑",
                                            desc: " ↓",
                                        }[header.column.getIsSorted() as string] ?? null} */}
                                        <span style={{ fontSize: "1em", marginLeft: 4 }}>
                                            {header.column.getIsSorted() === "asc" && " ↑"}
                                            {header.column.getIsSorted() === "desc" && " ↓"}
                                            {!header.column.getIsSorted() && "↕"}
                                        </span>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        {/* Filter Row */}

                        <TableRow sx={{ backgroundColor: "#ffffff" }}>
                            <TableCell></TableCell>

                            {/* {
                                table.getHeaderGroups()[0].headers.map((header) => {
                                    const colMeta = props.columns.find(
                                        (c) => c.columnName === header.column.id
                                    );

                                    if (
                                        colMeta?.multiline === true ||
                                        colMeta?.title === "Location" ||
                                        colMeta?.title === "Attachments"
                                    ) {
                                        return <TableCell key={header.id}></TableCell>;
                                    }
                                    // return <FilterHeaderCell key={header.id} header={header} memoizedDataProp={memoizedData} />;
                                })} */}
                            {
                                leafHeaders.map((header) => {
                                    const colMeta = colMetaById.get(header.column.id);

                                    if (colMeta?.multiline || colMeta?.title === "Location" || colMeta?.title === "Attachments") {
                                        return <TableCell key={header.id} />;
                                    }

                                    return (
                                        <TableCell key={header.id} onClick={(e) => e.stopPropagation()}>
                                            <DropdownForTanStackFilter
                                                options={optionsByColumnId.get(header.column.id) ?? []}
                                                committed={(header.column.getFilterValue() as string[] | undefined) ?? []}
                                                onCommit={(val) => header.column.setFilterValue(val)}
                                            />
                                        </TableCell>
                                    );
                                })
                            }

                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {table.getRowModel().rows.map((row, idx) => {
                            const editable = isLogAlwaysEditable ? 'yes' : row.original.isEditable ?? row.original.Editable;
                            const locked = !isYes(editable);
                            const isLockedRow = (!props.isConsolidatedTable || editable !== "yes") && locked;

                            return (

                                <TableRow
                                    key={row.id}

                                    // sx={{
                                    //     backgroundColor: idx % 2 === 0 ? "#4BB75F" : "#ffffff",
                                    //     //color: idx % 2 === 0 ? "#000000" : "#000000",
                                    //     // dim + hint when locked
                                    //     ...((!props.isConsolidatedTable || editable !== 'yes') && locked && {
                                    //         opacity: 0.55,
                                    //         filter: "grayscale(0.25)",
                                    //     }),
                                    //     cursor: (!props.isConsolidatedTable || editable !== 'yes') && locked ? "not-allowed" : "default",
                                    // }}
                                    sx={{
                                        ...rowZebraBrandSx(idx, row),
                                        ...(isLockedRow ? rowLockedBrandSx : { cursor: "default" }),

                                        // hover only if editable
                                        "&:hover": isLockedRow ? {} : { filter: "brightness(0.98)" }, // optional
                                    }}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedSet.has(getRowKey(row.original))}
                                            onChange={() => toggleRow(row.original)}
                                            sx={{
                                                "&.Mui-checked": { color: "#007bff" },
                                            }}
                                        />
                                    </TableCell>

                                    {row.getVisibleCells().map((cell) => {
                                        //const colMeta = props.columns.find(c => c.columnName === cell.column.id);
                                        const colMeta = colMetaById.get(cell.column.id);
                                        //const value = flexRender(cell.column.columnDef.cell, cell.getContext());
                                        //const rawValue = row.original[cell.column.id];
                                        const raw = cell.getValue(); //row.original[cell.column.id];
                                        const text = raw == null ? "" : String(raw);

                                        const isLongText = colMeta?.multiline === true || colMeta?.title === "Comments" || colMeta?.title === "Description";
                                        // Special case for Location
                                        if (colMeta?.columnName === "Location" && typeof text === "string" && text.startsWith("https://maps.google")) {
                                            return (
                                                <TableCell key={cell.id} sx={{ maxWidth: 200, textAlign: "center" }}>
                                                    <TouchableOpacity onPress={() => window.open(text, "_blank")}>
                                                        <Tooltip title="Open Location">
                                                            <IconButton
                                                                icon="map-marker-path"
                                                                iconColor={MD3Colors.error10}
                                                                size={20}
                                                                onPress={() => window.open(text, "_blank")}
                                                            />
                                                        </Tooltip>
                                                    </TouchableOpacity>
                                                </TableCell>
                                            );
                                        }

                                        // Default rendering (with tooltip/ellipsis)
                                        return (
                                            <TableCell
                                                key={cell.id}
                                                sx={cellSx}
                                            >
                                                {/* <Tooltip title={value} placement="top" arrow>
                                                    <span
                                                        style={{
                                                            display: "inline-block",
                                                            maxWidth: "200px",
                                                            whiteSpace: colMeta?.multiline ? "normal" : "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            color: "inherit",
                                                        }}
                                                    >
                                                        {value}
                                                    </span>
                                                </Tooltip> */}
                                                {isLongText ? (
                                                    <Tooltip title={text} placement="top" arrow>
                                                        <span style={clampCellSx as any}>{text}</span>
                                                    </Tooltip>
                                                ) : (
                                                    // <Tooltip title={text} placement="top" arrow>
                                                    // <span style={{ display: "inline-block", maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    //     {text}
                                                    // </span>

                                                    <span style={nowrapSpanStyle}>
                                                        {text}
                                                    </span>

                                                    // </Tooltip>
                                                )}
                                            </TableCell>

                                        );
                                    })}
                                </TableRow>
                            )
                        })
                        }
                    </TableBody>

                </Table>
            </TableContainer>

            {/* Pagination */}
            <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                    <Button
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        Prev
                    </Button>
                    <span style={{ margin: "0 8px" }}>
                        Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                    </span>
                    <Button
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        Next
                    </Button>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {/* ✅ Page size selector */}
                    <span>Rows per page:</span>
                    <Select
                        size="small"
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => table.setPageSize(Number(e.target.value))}
                    >
                        {[30, 50, 100].map((pageSize) => (
                            <MenuItem key={pageSize} value={pageSize}>
                                {pageSize}
                            </MenuItem>
                        ))}
                    </Select>

                    {/* ✅ Item count */}
                    <span>
                        Showing <b>{table.getFilteredRowModel().rows.length}</b> of{" "}
                        <b>{table.getCoreRowModel().rows.length}</b> items
                    </span>
                </div>
            </Toolbar>


        </Paper>
    );
};

export default ReactDataTable;
